import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Elements, useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';
import { getStripe } from '../lib/stripe.js';
import { api, ApiError } from '../lib/api.js';
import { AuthShell } from '../components/AuthShell.jsx';
import { Stepper } from '../components/Stepper.jsx';
import './SignupPage.css';

/**
 * Two-step signup flow.
 *
 * Step 1: user fills { companyName, fullName, email, password }.
 *         We POST to /api/signup.
 *         Backend creates tenant + admin + Stripe customer + SetupIntent.
 *         Backend also fires verify email (async, user sees "check email"
 *         only AFTER card is captured in step 2).
 *         Response contains SetupIntent.client_secret.
 *
 * Step 2: Stripe Elements embedded inline. User confirms card.
 *         stripe.confirmSetup() attaches the card to the customer.
 *         On success, navigate to /signup/pending (magic link has been
 *         sent during step 1, user now waits for email).
 *
 * Design choice: we create the tenant IN STEP 1, not step 2. Reason: if
 * the user bails before confirming the card, we still have their email
 * and can recover them via "reset password" or "card setup" links later.
 * Lose-the-tenant-on-card-cancellation is worse business outcome than
 * having a half-provisioned tenant row with no payment method.
 *
 * Phase 3 handles the "no payment method captured" case: that tenant's
 * trial_ends_at will expire without a subscription being created, so
 * they end up in 'trial_expired' status and get reminder emails.
 */
export function SignupPage() {
  const [step, setStep] = useState(1);
  const [signupData, setSignupData] = useState(null); // { clientSecret, tenantId, tenantSlug, email }

  return (
    <AuthShell>
      <div className="auth-card">
        <Stepper current={step} />

        {step === 1 && (
          <StepOne
            onComplete={(data) => {
              setSignupData(data);
              setStep(2);
            }}
          />
        )}

        {step === 2 && signupData && (
          <Elements
            stripe={getStripe()}
            options={{
              clientSecret: signupData.clientSecret,
              appearance: stripeAppearance,
              fonts: stripeFonts,
            }}
          >
            <StepTwo signupData={signupData} onBack={() => setStep(1)} />
          </Elements>
        )}
      </div>
    </AuthShell>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Step 1 — account details                                                   */
/* ─────────────────────────────────────────────────────────────────────────── */

function StepOne({ onComplete }) {
  const [form, setForm] = useState({
    companyName: '',
    fullName: '',
    email: '',
    password: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  function set(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors(fe => ({ ...fe, [field]: null }));
    }
  }

  function validateClient(f = form) {
    const errs = {};
    if (!f.companyName.trim() || f.companyName.trim().length < 2) {
      errs.companyName = 'Your business name is required';
    }
    if (!f.fullName.trim() || f.fullName.trim().length < 2) {
      errs.fullName = 'Your name is required';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      errs.email = 'Please enter a valid email address';
    }
    if (f.password.length < 10) {
      errs.password = 'Password must be at least 10 characters';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    // Defensive: sync form state from DOM before validating.
    // Some browsers (Chrome, Safari) autofill inputs without firing
    // React's onChange, so form.X can be '' while the input shows text.
    const formEl = e.target;
    const patched = { ...form };
    for (const field of ['companyName', 'fullName', 'email', 'password']) {
      const input = formEl.querySelector(`[name="${field}"]`);
      if (input && input.value && !patched[field]) {
        patched[field] = input.value;
      }
    }
    if (JSON.stringify(patched) !== JSON.stringify(form)) {
      setForm(patched);
    }
    // Use patched values for validation
    const formToValidate = patched;

    if (!validateClient(formToValidate)) return;

    setSubmitting(true);
    try {
      // Backend expects fullName field, maps to admins.full_name.
      // companyName is used for tenant name + slug generation.
      const data = await api.post('/api/signup', {
        companyName: formToValidate.companyName.trim(),
        adminName: formToValidate.fullName.trim(),
        email: formToValidate.email.trim(),
        password: formToValidate.password,
        plan: 'starter',
      });

      if (!data?.setupIntent?.client_secret) {
        throw new Error('Signup succeeded but no payment setup returned. Please contact support.');
      }

      onComplete({
        clientSecret: data.setupIntent.client_secret,
        tenantId: data.tenantId,
        tenantSlug: data.tenantSlug,
        email: data.email,
      });
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Duplicate email — highlight the field
        setFieldErrors(fe => ({ ...fe, email: e.message }));
      } else if (e instanceof ApiError) {
        setError(e.message);
      } else {
        setError('Something went wrong. Please try again or email jez@tocynid.com if it keeps happening.');
      }
      setSubmitting(false);
    }
  }

  return (
    <>
      <p className="eyebrow" style={{ marginBottom: 'var(--sp-2)' }}>Step 1 of 2</p>
      <h1 className="auth-heading">Start your 14-day trial.</h1>
      <p className="auth-subheading">
        No charge during your trial. Cancel any time from your dashboard.
      </p>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <form onSubmit={handleSubmit} noValidate>
        <Field
          label="Business name"
          hint="What we'll call your organisation on cards and verification pages."
          name="companyName"
          value={form.companyName}
          onChange={v => set('companyName', v)}
          error={fieldErrors.companyName}
          autoComplete="organization"
          autoFocus
        />

        <Field
          label="Your name"
          name="fullName"
          value={form.fullName}
          onChange={v => set('fullName', v)}
          error={fieldErrors.fullName}
          autoComplete="name"
        />

        <Field
          label="Work email"
          hint="We'll send a verification link here. We never share your email."
          type="email"
          name="email"
          value={form.email}
          onChange={v => set('email', v)}
          error={fieldErrors.email}
          autoComplete="email"
          inputMode="email"
        />

        <Field
          label="Password"
          hint="At least 10 characters. A memorable phrase works well."
          type="password"
          name="password"
          value={form.password}
          onChange={v => set('password', v)}
          error={fieldErrors.password}
          autoComplete="new-password"
        />

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={submitting}
          aria-busy={submitting}
        >
          {submitting ? 'Creating your account…' : 'Continue to payment'}
        </button>

        <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-5)', textAlign: 'center' }}>
          Already have an account? <a href="/login">Log in</a>
        </p>

        <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-3)', textAlign: 'center' }}>
          By continuing you agree to our <a href="https://tocynid.com/terms.html" target="_blank" rel="noopener noreferrer">terms</a> and <a href="https://tocynid.com/privacy.html" target="_blank" rel="noopener noreferrer">privacy policy</a>.
        </p>
      </form>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Step 2 — payment                                                           */
/* ─────────────────────────────────────────────────────────────────────────── */

function StepTwo({ signupData, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    if (!stripe || !elements) {
      setError('Payment form is still loading. Please try again in a moment.');
      return;
    }

    setSubmitting(true);
    try {
      // confirmSetup handles the SetupIntent confirmation. On success, Stripe
      // redirects to return_url. We don't actually need the redirect for the
      // happy path (we handle the response inline), so we pass redirect: 'if_required'
      // to only redirect for 3DS / SCA flows.
      const { error: stripeError, setupIntent } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/signup/pending?e=${encodeURIComponent(signupData.email)}`,
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        // Card declined, 3DS failed, etc.
        setError(stripeError.message || 'Your payment method could not be set up. Please check the details and try again.');
        setSubmitting(false);
        return;
      }

      if (setupIntent && setupIntent.status === 'succeeded') {
        // Card captured! The webhook will asynchronously persist the
        // payment_method_id to our tenants table. User goes to pending screen.
        navigate(`/signup/pending?e=${encodeURIComponent(signupData.email)}`);
        return;
      }

      // Uncommon paths — setupIntent is requires_action etc. The redirect
      // would have handled this; if we're here without a redirect, something
      // unusual happened.
      setError('Payment setup is taking longer than expected. Please refresh and try again.');
      setSubmitting(false);
    } catch (e) {
      setError('Payment setup failed. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <>
      <p className="eyebrow" style={{ marginBottom: 'var(--sp-2)' }}>Step 2 of 2</p>
      <h1 className="auth-heading">Add a payment method.</h1>
      <p className="auth-subheading">
        Your card is saved but not charged. We bill you the first time 14 days
        after today, only if you still have the account.
      </p>

      {error && <div className="alert alert-error" role="alert">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 'var(--sp-5)' }}>
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={submitting || !stripe || !elements}
          aria-busy={submitting}
        >
          {submitting ? 'Saving card…' : 'Save card and start trial'}
        </button>

        <button
          type="button"
          className="btn btn-ghost btn-block mt-4"
          onClick={onBack}
          disabled={submitting}
        >
          ← Back to account details
        </button>

        <p className="muted" style={{ fontSize: 'var(--fs-xs)', marginTop: 'var(--sp-5)', textAlign: 'center' }}>
          Payments secured by <strong>Stripe</strong>. We never see your card number.
          Card details live on Stripe's servers, not ours.
        </p>
      </form>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Small components                                                           */
/* ─────────────────────────────────────────────────────────────────────────── */

function Field({ label, hint, name, type = 'text', value, onChange, error, ...rest }) {
  const id = `f-${name}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      {hint && <div className="field-hint" id={hintId}>{hint}</div>}
      <input
        id={id}
        className="field-input"
        type={type}
        name={name}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
        {...rest}
      />
      {error && <div className="field-error" id={errorId} role="alert">{error}</div>}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────── */
/*  Stripe Elements theming                                                    */
/* ─────────────────────────────────────────────────────────────────────────── */

const stripeAppearance = {
  theme: 'flat',
  variables: {
    colorPrimary:       '#00AB39',
    colorBackground:    '#FAF6EE',
    colorText:          '#0E1A15',
    colorDanger:        '#E63946',
    fontFamily:         "'Manrope', sans-serif",
    fontSizeBase:       '16px',
    spacingUnit:        '4px',
    borderRadius:       '8px',
    colorTextPlaceholder: '#6B7A70',
    colorTextSecondary:   '#3D4B43',
  },
  rules: {
    '.Input': {
      border: '1px solid #D9CFBC',
      padding: '12px 14px',
      backgroundColor: '#FAF6EE',
    },
    '.Input:focus': {
      border: '1px solid #00AB39',
      boxShadow: '0 0 0 3px #E4F7E8',
      backgroundColor: '#FFFFFF',
    },
    '.Label': {
      fontWeight: '600',
      fontSize: '14px',
      marginBottom: '8px',
      color: '#0E1A15',
    },
    '.Tab': {
      border: '1px solid #D9CFBC',
      backgroundColor: '#FAF6EE',
    },
    '.Tab--selected': {
      borderColor: '#00AB39',
      backgroundColor: '#E4F7E8',
      color: '#008A2E',
    },
  },
};

const stripeFonts = [
  { cssSrc: 'https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700&display=swap' },
];
